
import React from "react";
import FileContentRenderer from "./FileContentRenderer";
import FeedbackContentRenderer from "./FeedbackContentRenderer";
import RegularContentRenderer from "./RegularContentRenderer";
import MessageTimestamp from "./MessageTimestamp";
import { isEnhancedFeedbackAnalysis } from "../../utils/enhancedFeedbackDetection";
import { ProcessedFile } from "../../context/types";

interface ChatMessageProps {
  content: string | React.ReactNode;
  type: "system" | "user";
  timestamp: number;
  files?: ProcessedFile[];
  targetId?: string;
  targetType?: 'location' | 'image' | 'prediction';
  unsplashData?: {
    id: string;
    photographer: string;
    photographerUsername: string;
    photographerUrl: string;
    unsplashUrl: string;
    downloadUrl: string;
    downloadEndpoint: string;
    description: string;
  };
}

const ChatMessage: React.FC<ChatMessageProps> = ({ content, type, timestamp, files, targetId, targetType, unsplashData }) => {
  console.log('🎬 CHATMESSAGE-RENDER: Component received:', {
    messageType: type,
    contentType: typeof content,
    contentLength: typeof content === 'string' ? content.length : 0,
    hasFiles: !!files,
    filesCount: files?.length || 0,
    messageId: `${type}-${Date.now()}`,
    files: files?.map(f => ({
      type: f.type,
      filename: f.filename,
      contentLength: f.content.length,
      isValidUrl: f.content.startsWith('http'),
      isValidBase64: f.content.startsWith('data:image/'),
      contentPreview: f.content.substring(0, 100)
    }))
  });

  // Enhanced debugging for file upload testing
  if (files && files.length > 0) {
    console.log('✅ CHATMESSAGE-DEBUG: Files detected for display!', {
      fileCount: files.length,
      fileDetails: files.map((f, i) => ({
        index: i,
        type: f.type,
        filename: f.filename,
        contentType: f.content.startsWith('data:') ? 'base64' : 'url',
        contentLength: f.content.length
      }))
    });
  } else {
    console.log('⚠️ CHATMESSAGE-DEBUG: No files to display', {
      filesProvided: !!files,
      filesLength: files?.length || 0
    });
  }

  const renderContent = () => {
    if (typeof content !== "string") {
      console.log('🎬 CHATMESSAGE-RENDER: Content is not string, returning as-is');
      return content;
    }

    // ENHANCED: Handle attached files with comprehensive logging and fallback support
    if (files && files.length > 0) {
      return <FileContentRenderer content={content} type={type} files={files} />;
    }

    // FALLBACK: Process content for embedded images and other content
    console.log('📝 CHATMESSAGE-RENDER: No files or no valid images - processing content for embedded data');

    // Handle feedback analysis with enhanced detection and complete bypass
    if (type === "system" && isEnhancedFeedbackAnalysis(content)) {
      console.log('🎯 CHATMESSAGE-RENDER: Enhanced feedback detected - using FeedbackContentRenderer');
      return <FeedbackContentRenderer content={content} targetId={targetId} targetType={targetType} />;
    }

    return <RegularContentRenderer content={content} type={type} unsplashData={unsplashData} />;
  };

  return (
    <div className={`flex ${type === "user" ? "justify-end" : "justify-start"} mb-4`}>
      <div
        className={`max-w-[80%] p-4 text-base leading-6 break-words ${
          type === "user" 
            ? "bg-muted text-muted-foreground rounded-[16px_16px_0px_16px]"
            : "bg-background text-card-foreground rounded-2xl"
        }`}
      >
        {renderContent()}
        <MessageTimestamp timestamp={timestamp} type={type} />
      </div>
    </div>
  );
};

export default ChatMessage;
